import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EnvelopeIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { authService } from '../../services';
import { Button, Card, CardBody } from '../../components/ui';

const COOLDOWN_SECONDS = 60; // 60 seconds cooldown between resends

export default function VerifyEmail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownIntervalRef = useRef(null);
  const [token, setToken] = useState(() => {
    const urlToken = searchParams.get('token');
    return urlToken ? urlToken.trim() : null;
  });
  const email = location.state?.email || searchParams.get('email') || '';
  const hasVerifiedRef = useRef(false);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, []);

  // Start cooldown timer
  const startCooldown = () => {
    setCooldown(COOLDOWN_SECONDS);
    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current);
    }
    cooldownIntervalRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken && !hasVerifiedRef.current && !verified && !loading) {
      const cleanToken = urlToken.trim();
      setToken(cleanToken);
      hasVerifiedRef.current = true;
      verifyEmail(cleanToken);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const verifyEmail = async (tokenToVerify = null) => {
    const tokenValue = tokenToVerify || token;
    if (!tokenValue) return;

    setLoading(true);
    try {
      // Clean token - trim and handle URL encoding if needed
      let cleanToken = tokenValue.trim();
      try {
        // Try to decode if it's URL encoded, otherwise use as-is
        cleanToken = decodeURIComponent(cleanToken);
      } catch (e) {
        // If decode fails, token is not URL encoded, use as-is
        cleanToken = tokenValue.trim();
      }

      const response = await authService.verifyEmail(cleanToken);
      if (response.data?.accessToken) {
        setVerified(true);
        toast.success(t('auth.verificationSuccess'));
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || t('errors.serverError');
      toast.error(errorMessage);
      // If token is invalid/expired, allow user to resend
      if (errorMessage.includes('token') || errorMessage.includes('رمز') || errorMessage.includes('Invalid')) {
        setToken(null);
        hasVerifiedRef.current = false;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error(t('auth.emailRequired'));
      return;
    }

    setResending(true);
    try {
      await authService.resendVerification(email);
      toast.success(t('auth.verificationSent'));
      startCooldown();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('auth.verifyEmail')} - ProCTM</title>
      </Helmet>

      <Card className="w-full max-w-md">
        <CardBody className="p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-neutral-dark">
              {verified ? t('auth.emailVerified') : t('auth.verifyEmail')}
            </h1>
            <p className="mt-2 text-gray-500">
              {verified ? t('auth.redirecting') : t('auth.verifyEmailDesc')}
            </p>
          </div>

          {verified ? (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-success/10 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-8 h-8 text-success" />
              </div>
              <h2 className="text-lg font-semibold text-neutral-dark mb-2">
                {t('auth.emailVerified')}
              </h2>
              <p className="text-gray-500 mb-6">
                {t('auth.verificationSuccess')}
              </p>
              <Button onClick={() => navigate('/dashboard')} className="w-full">
                {t('common.goToDashboard')}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {token ? (
                <>
                  {loading ? (
                    <div className="text-center">
                      <ArrowPathIcon className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                      <p className="text-gray-600">{t('auth.verifying')}</p>
                      <p className="text-sm text-gray-500 mt-2">
                        {t('auth.verifyingDesc')}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center space-y-4">
                      <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                        <EnvelopeIcon className="w-8 h-8 text-primary" />
                      </div>
                      <p className="text-gray-600 mb-4">
                        {t('auth.verifyEmailInstructions')}
                      </p>
                      <Button onClick={() => verifyEmail(token)} className="w-full" loading={loading}>
                        {t('auth.verifyEmail')}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                      <EnvelopeIcon className="w-8 h-8 text-primary" />
                    </div>
                    <h2 className="text-lg font-semibold text-neutral-dark mb-2">
                      {t('auth.checkYourEmail')}
                    </h2>
                    <p className="text-gray-600 mb-2">
                      {email ? (
                        <>
                          {t('auth.verificationSentTo')}{' '}
                          <span className="font-medium text-neutral-dark">{email}</span>
                        </>
                      ) : (
                        t('auth.verificationSentCheck')
                      )}
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                      {t('auth.clickLinkInEmail')}
                    </p>
                  </div>

                  {email && (
                    <div className="pt-6 border-t border-gray-200">
                      <p className="text-sm text-gray-500 mb-4 text-center">
                        {t('auth.didntReceiveEmail')}
                      </p>
                      <Button
                        variant="outline"
                        onClick={handleResend}
                        loading={resending}
                        disabled={cooldown > 0 || resending}
                        className="w-full"
                      >
                        {cooldown > 0
                          ? t('auth.resendCooldown', { seconds: cooldown })
                          : t('auth.resendVerification')}
                      </Button>
                    </div>
                  )}
                </>
              )}

              <p className="mt-6 text-center text-sm text-gray-500">
                <Link to="/login" className="text-primary font-medium hover:underline">
                  {t('auth.backToLogin')}
                </Link>
              </p>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
